import { GridColumn as Column } from '@progress/kendo-react-grid';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { DropdownCell } from 'src/components/shared/DataGrid/EditableGrid/DropdownCell';
import { EditableGrid } from 'src/components/shared/DataGrid/EditableGrid/EditableGrid';
import { selectMethods } from 'src/store/methods/methods.selectors';
import { selectActiveParameters } from 'src/store/parameters/parameters.selectors';
import {
  ParameterSetDetailDto,
  ParameterSetParameterDto
} from 'src/store/parametersets/parameterset';
import { SpecificationParameterDto } from 'src/store/specifications/specification';

interface Props {
  parameterSet: Nullable<ParameterSetDetailDto>;
  onParametersChanged: (parameters: ParameterSetParameterDto[]) => void;
}

let uniqueId = 0;

export const StandardPractice: React.FC<Props> = ({
  parameterSet,
  onParametersChanged
}) => {
  const { t } = useTranslation();

  const [parameters, setParameters] = useState<ParameterSetParameterDto[]>([]);
  const [editedParameters, setEditedParameters] = useState<
    ParameterSetParameterDto[]
  >([]);

  const parameters$ = useSelector(selectActiveParameters);
  const methods$ = useSelector(selectMethods);

  useEffect(() => {
    if (parameterSet) {
      setParameters(parameterSet.parameterSetParameters ?? []);
      setEditedParameters(parameterSet.parameterSetParameters ?? []);
    }
  }, [parameterSet]);

  useEffect(() => {
    onParametersChanged(editedParameters);
  }, [editedParameters, onParametersChanged]);

  const onCancel = () => {
    setParameters([...(parameterSet?.parameterSetParameters ?? [])]);
  };

  const onAddParameter = () => {
    const parameter: ParameterSetParameterDto = {
      id: --uniqueId,
      column: null,
      parameterSetId: parameterSet?.id,
      parameterId: null,
      methodId: null,
      parameterName: '',
      methodName: ''
    };
    setParameters([parameter, ...editedParameters]);
  };

  const onDeleteParameter = (parameter: SpecificationParameterDto) => {
    setParameters(parameters.filter(p => p.id !== parameter.id));
    setEditedParameters(editedParameters.filter(p => p.id !== parameter.id));
  };

  return (
    <Fragment>
      <div className="flex mb-24">
        <SecondaryButton
          icon="la-plus"
          onClick={onAddParameter}
          className="mr-24"
        >
          {t('add.verb')}
        </SecondaryButton>
        <TertiaryButton onClick={onCancel}>{t('undo')}</TertiaryButton>
      </div>
      <EditableGrid
        items={parameters}
        onItemsChange={items => setEditedParameters(items)}
      >
        <Column
          field="column"
          title={t('props.column')}
          width="180px"
          editor="numeric"
        />
        <Column
          field="parameterId"
          title={t('parameter.singular')}
          width="320px"
          cell={props => (
            <DropdownCell
              {...props}
              items={parameters$}
              uniqueField="id"
              optionField="name"
            />
          )}
        />
        <Column
          field="methodId"
          title={t('method.singular')}
          width="320px"
          cell={props => (
            <DropdownCell
              {...props}
              items={methods$}
              uniqueField="id"
              optionField="name"
            />
          )}
        />
        <Column
          filterable={false}
          editable={false}
          cell={props => (
            <td style={{ textAlign: 'right' }}>
              <DeleteButton onClick={() => onDeleteParameter(props.dataItem)} />
            </td>
          )}
        />
      </EditableGrid>
    </Fragment>
  );
};
